import React, { useState, useEffect } from 'react';
import { Sun, Moon } from 'lucide-react';
import Game from './components/Game';
import Store from './components/Store';
import Leaderboard from './components/Leaderboard';
import DailyChallenge from './components/DailyChallenge';
import AdUnit from './components/AdUnit';
import { navButton, iconButton } from './utils/buttonStyles';
import { AD_SLOTS } from './utils/ads';

const PLAYER_STATE_KEY = 'sudokuPlayerState';
const DARK_MODE_KEY = 'sudokuDarkMode';

function loadPlayerState() {
  try {
    const raw = localStorage.getItem(PLAYER_STATE_KEY);
    if (!raw) return { points: 10, streak: 0 };
    return JSON.parse(raw);
  } catch {
    return { points: 10, streak: 0 };
  }
}

function App() {
  const [view, setView] = useState('game'); // 'game', 'store', 'leaderboard'
  const initial = loadPlayerState();
  const [points, setPoints] = useState(initial.points);
  const [streak, setStreak] = useState(initial.streak);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    localStorage.setItem(PLAYER_STATE_KEY, JSON.stringify({ points, streak }));
  }, [points, streak]);

  useEffect(() => {
    const storedIsDark = localStorage.getItem(DARK_MODE_KEY) === 'true';
    document.documentElement.classList.toggle('dark', storedIsDark);
    setIsDark(storedIsDark);
  }, []);

  const toggleDarkMode = () => {
    const next = document.documentElement.classList.toggle('dark');
    localStorage.setItem(DARK_MODE_KEY, next ? 'true' : 'false');
    setIsDark(next);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <header className="text-center mb-8">
        <p className="text-sm font-semibold tracking-wide text-purple-600 dark:text-purple-400">SudokuPuzzle.pro</p>
        <h1 className="text-4xl font-bold text-purple-600 dark:text-purple-400">Free Online Sudoku</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">Infinite Puzzles. Infinite Glory.</p>
        <div className="flex justify-center items-center space-x-4 mt-2 text-gray-700 dark:text-gray-200">
          <span>Points: {points}</span>
          <span>Streak: {streak} days</span>
          <button onClick={toggleDarkMode} className={iconButton} aria-label="Toggle dark mode">
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>
      <nav className="flex justify-center flex-wrap gap-3 mb-4">
        <button onClick={() => setView('game')} className={navButton(view === 'game')}>Play</button>
        <button onClick={() => setView('store')} className={navButton(view === 'store')}>Store</button>
        <button onClick={() => setView('leaderboard')} className={navButton(view === 'leaderboard')}>Leaderboard</button>
        <button onClick={() => setView('daily')} className={navButton(view === 'daily')}>Daily Challenge</button>
        <a href="/learn" className={navButton(false)}>Learn How to Play</a>
      </nav>
      {view === 'game' && <Game points={points} setPoints={setPoints} streak={streak} setStreak={setStreak} />}
      {view === 'store' && <Store points={points} />}
      {view === 'leaderboard' && <Leaderboard />}
      {view === 'daily' && <DailyChallenge points={points} setPoints={setPoints} streak={streak} setStreak={setStreak} />}
      <div className="max-w-3xl mx-auto mt-6 min-h-[90px]">
        <AdUnit slot={AD_SLOTS.gameFooter} />
      </div>
      <footer className="text-center mt-8 text-sm text-gray-500 dark:text-gray-400">
        <p>
          <a href="/learn" className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 underline-offset-2 hover:underline">
            Learn How to Play
          </a>
          {' · '}
          <a href="/privacy/" className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 underline-offset-2 hover:underline">
            Privacy
          </a>
        </p>
        <p className="mt-2">© 2026 SudokuPuzzle.pro - Play smarter. Solve faster.</p>
      </footer>
    </div>
  );
}

export default App;
